import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, X } from 'lucide-react';
import { usersService } from '@/services/users.service';
import { cn } from '@/lib/utils';
import { initials, fullName } from './taskConstants';

interface AssigneeSequenceListProps {
  /** Ordered user ids — position 0 is the first person the task goes to. */
  selected: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Extra classes for the outer container, so a parent can bound and scroll the chain. */
  className?: string;
}

/**
 * Ordered fallback chain for an Important Task. The task goes to the first person here
 * who is punched in; if they aren't, it falls through to the next.
 */
export function AssigneeSequenceList({
  selected,
  onChange,
  disabled,
  className,
}: AssigneeSequenceListProps) {
  const { data } = useQuery({
    queryKey: ['tasks', 'assignee-picker'],
    queryFn: () => usersService.picker(),
  });

  function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= selected.length) return;
    const next = [...selected];
    [next[index], next[target]] = [next[target]!, next[index]!];
    onChange(next);
  }

  if (selected.length === 0) {
    return (
      <p className={cn(
        'flex items-center justify-center rounded-lg border border-dashed border-border/60 px-3 py-3 text-center text-xs text-muted-foreground',
        className,
      )}>
        Pick people below — the order you pick becomes the fallback sequence.
      </p>
    );
  }

  return (
    // One bordered container with divided rows rather than one bordered card per person:
    // a long chain was rendering as a stack of ~11 separate outlines, which read as noise.
    // The first row keeps its tint, so the position that actually matters still stands out.
    <ol className={cn(
      'divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60 bg-card',
      className,
    )}>
      {selected.map((id, i) => {
        const u = data?.find((x) => x.id === id);
        return (
          <li
            key={id}
            className={cn(
              'flex items-center gap-2 px-2 py-1.5',
              i === 0 && 'bg-primary/5',
            )}
          >
            {/* Only position 1 gets the solid teal badge. Filling all eleven made the
                column read as a block of teal dots with no first-choice to find. */}
            <span className={cn(
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold tabular-nums',
              i === 0
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground',
            )}>
              {i + 1}
            </span>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
              {initials(u?.first_name ?? null, u?.last_name ?? null)}
            </span>
            {/* One line: "First choice" is an inline chip rather than a second row. It only
                ever applies to position 1, and stacking it made EVERY row tall enough for
                two lines. The number, teal border and tint already mark this row. */}
            <span className="flex min-w-0 flex-1 items-center gap-1.5">
              <span className="truncate text-sm font-medium text-foreground">
                {u ? fullName(u.first_name, u.last_name) : 'Loading…'}
              </span>
              {i === 0 && (
                <span className="shrink-0 rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-semibold text-primary">
                  First
                </span>
              )}
            </span>
            {!disabled && (
              <span className="flex shrink-0 items-center gap-0.5">
                <button
                  type="button" aria-label="Move up" disabled={i === 0}
                  onClick={() => move(i, -1)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button" aria-label="Move down" disabled={i === selected.length - 1}
                  onClick={() => move(i, 1)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button" aria-label="Remove"
                  onClick={() => onChange(selected.filter((x) => x !== id))}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/15"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
